"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import Link from "next/link";
import { ChevronDown, Columns2, LoaderCircle, Mic, Lightbulb, NotebookPen } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useWritingTools } from "./writing-tools-context";
import { useDictationVisibility } from "./dictation-visibility-provider";
import { ChapterNotesSwitchIndicator, MobileWritingToolsMenu } from "./mobile-writing-tools-menu";
const itemClass = "min-h-10 gap-2.5 rounded-md px-2.5 py-2 text-[13px] text-zinc-700 focus:bg-zinc-100 focus:text-zinc-900 dark:text-zinc-200 dark:focus:bg-zinc-800 dark:focus:text-zinc-50";
const labelClass = "px-2.5 pb-1.5 pt-2 text-[10px] font-medium uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-400";
const iconClass = "size-4 text-zinc-400 dark:text-zinc-500";
export function WritingTools(t0) {
  const $ = _c(8);
  const {
    mobile: t1
  } = t0;
  const mobile = t1 === undefined ? false : t1;
  const triggerRef = useRef(null);
  const tools = useWritingTools();
  const {
    isVisible
  } = useDictationVisibility();
  if (!tools) {
    return null;
  }
  let t2;
  if ($[0] !== tools) {
    t2 = function selectTool(tool) {
      tools?.setActiveTool(tools.activeTool === tool ? null : tool);
    };
    $[0] = tools;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selectTool = t2;
  let t3;
  if ($[2] !== isVisible || $[3] !== mobile || $[4] !== selectTool || $[5] !== tools.activeTool || $[6] !== tools.chapterNotes) {
    t3 = <>{mobile ? <MobileWritingToolsMenu triggerRef={triggerRef} dictationEnabled={isVisible} activeTool={tools.activeTool} onSelect={selectTool} chapterNotes={tools.chapterNotes} /> : <DropdownMenu><DropdownMenuTrigger ref={triggerRef} data-writing-tools-trigger={true} className="inline-flex min-h-9 items-center justify-center gap-1 rounded-md px-2 text-xs font-medium text-zinc-600 hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-zinc-400 dark:text-zinc-400 dark:hover:bg-zinc-800">Writing tools <ChevronDown className="size-3" aria-hidden={true} /></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-64 rounded-xl border-zinc-200 bg-white p-1.5 text-zinc-900 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100" onCloseAutoFocus={event => {
          const trigger = triggerRef.current;
          if (!trigger?.closest("[hidden]")) {
            return;
          }
          const visibleTrigger = trigger.closest("[data-chapter-workspace]")?.querySelector("[data-writing-tools-trigger]");
          if (visibleTrigger) {
            event.preventDefault();
            visibleTrigger.focus();
          }
        }}>{tools.chapterNotes && <><DropdownMenuLabel className={labelClass}>Documents</DropdownMenuLabel><DropdownMenuItem className={itemClass} role="menuitemcheckbox" aria-checked={tools.chapterNotes.enabled} disabled={tools.chapterNotes.disabled} onSelect={event_0 => {
              const trigger_0 = triggerRef.current;
              const manuscriptTrigger = trigger_0?.closest("[data-chapter-workspace]")?.querySelector("[data-writing-tools-trigger]");
              if (!tools.chapterNotes?.enabled || !manuscriptTrigger || trigger_0 === manuscriptTrigger) {
                event_0.preventDefault();
              }
              tools.chapterNotes?.activate();
            }}>{tools.chapterNotes.loading ? <LoaderCircle className={`${iconClass} animate-spin`} aria-hidden={true} /> : <NotebookPen className={iconClass} aria-hidden={true} />}<span className="flex-1">{tools.chapterNotes.loading ? "Adding chapter notes\u2026" : "Chapter notes"}</span>{!tools.chapterNotes.loading && <ChapterNotesSwitchIndicator enabled={tools.chapterNotes.enabled} />}</DropdownMenuItem><DropdownMenuItem className={itemClass} role="menuitemcheckbox" aria-checked={!!tools.chapterNotes.sideBySide} disabled={!tools.chapterNotes.enabled || !tools.chapterNotes.toggleSideBySide || tools.chapterNotes.disabled} onSelect={event_1 => {
              event_1.preventDefault();
              tools.chapterNotes?.toggleSideBySide?.();
            }}><Columns2 className={iconClass} aria-hidden={true} /><span className="flex-1">View side by side</span><ChapterNotesSwitchIndicator enabled={!!tools.chapterNotes.sideBySide} /></DropdownMenuItem><DropdownMenuSeparator className="mx-1 my-1.5 bg-zinc-200 dark:bg-zinc-800" /></>}<DropdownMenuLabel className={labelClass}>Voice tools</DropdownMenuLabel>{isVisible ? <DropdownMenuItem className={itemClass} onSelect={() => selectTool("dictation")}><Mic className={iconClass} aria-hidden={true} /><span><span className="block">{tools.activeTool === "dictation" ? "Turn off Dictation" : "Dictation"}</span><span className="block text-xs text-zinc-500 dark:text-zinc-400">Turn speech into text</span></span></DropdownMenuItem> : <DropdownMenuItem className={itemClass} asChild={true}><Link href="/editor/settings"><Mic className={iconClass} aria-hidden={true} /><span>Enable dictation in settings</span></Link></DropdownMenuItem>}<DropdownMenuItem className={itemClass} onSelect={() => selectTool("brainstorming")}><Lightbulb className={iconClass} aria-hidden={true} /><span><span className="block">{tools.activeTool === "brainstorming" ? "Turn off Brainstorming" : "Brainstorming"}</span><span className="block text-xs text-zinc-500 dark:text-zinc-400">Speak and organize your ideas</span></span></DropdownMenuItem></DropdownMenuContent></DropdownMenu>}</>;
    $[2] = isVisible;
    $[3] = mobile;
    $[4] = selectTool;
    $[5] = tools.activeTool;
    $[6] = tools.chapterNotes;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}